<script setup>
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { formatDistanceToNow } from 'date-fns'
import TaskStatusBadge from './TaskStatusBadge.vue'

const props = defineProps({
  summaries: { type: Array, default: () => [] }
})

const formatRelative = (dateStr) => {
  if (!dateStr) return 'never'
  return formatDistanceToNow(new Date(dateStr), { addSuffix: false })
}

const healthColors = {
  healthy: 'var(--color-success)',
  degraded: 'var(--color-warning)',
  error: 'var(--color-destructive)'
}
</script>

<template>
  <div class="health-rail">
    <div v-for="tile in summaries" :key="tile.entityType" class="health-tile">
      <div class="tile-header">
        <span class="entity-label">{{ tile.entityType }}</span>
        <span class="health-dot" :style="{ background: healthColors[tile.healthStatus] }"></span>
      </div>
      <div class="tile-stats">
        <span class="active-count">{{ tile.activeCount }} active</span>
        <span class="failed-count" v-if="tile.failedCount > 0">{{ tile.failedCount }} failed</span>
        <span class="dl-count" v-if="tile.deadLetteredCount > 0">{{ tile.deadLetteredCount }} DL</span>
      </div>
      <div class="tile-timestamps">
        <span v-if="tile.lastSuccessAt">Last OK: {{ formatRelative(tile.lastSuccessAt) }}</span>
        <span v-if="tile.lastFailureAt" class="failure-time">Last fail: {{ formatRelative(tile.lastFailureAt) }}</span>
      </div>
      <RouterLink :to="`/app/tasks?entityType=${tile.entityType}`" class="drill-down">
        View
      </RouterLink>
    </div>
    <div v-if="summaries.length === 0" class="empty-rail">
      No task data available.
    </div>
  </div>
</template>

<style scoped>
.health-rail {
  display: flex; gap: 1rem; overflow-x: auto; padding-bottom: 0.5rem;
  flex-wrap: wrap;
}
.health-tile {
  flex: 1; min-width: 180px; max-width: 280px;
  padding: 0.75rem; border-radius: 8px; background: var(--color-card);
  border: 1px solid var(--color-border); display: flex; flex-direction: column; gap: 0.4rem;
}
.tile-header { display: flex; justify-content: space-between; align-items: center; }
.entity-label { font-weight: 600; font-size: 0.875rem; text-transform: capitalize; }
.health-dot { width: 8px; height: 8px; border-radius: 50%; }
.tile-stats { display: flex; gap: 0.5rem; font-size: 0.75rem; }
.active-count { color: var(--color-muted-foreground); }
.failed-count { color: var(--color-destructive); }
.dl-count { color: var(--color-muted-foreground); font-weight: 500; }
.tile-timestamps { display: flex; flex-direction: column; font-size: 0.7rem; color: var(--color-muted-foreground); }
.failure-time { color: var(--color-destructive); }
.drill-down { font-size: 0.75rem; color: var(--color-primary); text-decoration: none; }
.drill-down:hover { text-decoration: underline; }
.empty-rail { color: var(--color-muted-foreground); font-size: 0.875rem; padding: 1rem; }
</style>
